import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { useOrganisationBookingsQuery } from "@/hooks/useOrganisationBookingsQuery";
import { filterOrgBookings } from "./orgBookingsFilter";
import OrgBookingRow from "./OrgBookingRow";

interface OrgBookingsListProps {
  organisationId: string;
}

/**
 * Shared, self-contained component that fetches and displays bookings for an organisation.
 * Owns the search/filter box — all filtering is client-side over the loaded page.
 *
 * Consumed by the owner OrganisationView and the admin/PM org-detail surfaces.
 */
export default function OrgBookingsList({ organisationId }: OrgBookingsListProps) {
  const [query, setQuery] = useState("");
  // Debounce the typed query so filtering doesn't run on every keystroke.
  const [debouncedQuery, setDebouncedQuery] = useState("");

  const { data, isLoading, isError } = useOrganisationBookingsQuery({ organisationId });

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 200);
    return () => clearTimeout(t);
  }, [query]);

  if (isLoading) {
    return (
      <div data-testid="org-bookings-loading" className="py-8 text-center text-sm text-muted-foreground">
        Loading bookings…
      </div>
    );
  }

  if (isError) {
    return (
      <div data-testid="org-bookings-error" className="py-8 text-center text-sm text-destructive">
        Failed to load bookings.
      </div>
    );
  }

  const allBookings = data?.data ?? [];
  const filtered = filterOrgBookings(allBookings, debouncedQuery);

  return (
    <div className="space-y-3">
      <Input
        data-testid="org-bookings-search"
        placeholder="Search bookings…"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        className="bg-white"
      />

      {filtered.length === 0 ? (
        <div data-testid="org-bookings-empty" className="py-8 text-center text-sm text-muted-foreground">
          No bookings match your search.
        </div>
      ) : (
        <ul className="divide-y divide-border">
          {filtered.map((bk) => (
            <OrgBookingRow key={bk.id} booking={bk} />
          ))}
        </ul>
      )}
    </div>
  );
}
